type TargetCoordinates = {
  lat: number
  lng: number
}

type DraggingTargetMoveGuard = {
  phase: "dragging"
  position: TargetCoordinates
}

type SettlingTargetMoveGuard = {
  phase: "settling"
  position: TargetCoordinates
  expiresAt: number
}

type TargetMoveGuardState = DraggingTargetMoveGuard | SettlingTargetMoveGuard

export const TARGET_MOVE_SETTLE_WINDOW_MS = 1500
export const TARGET_MOVE_COORDINATE_EPSILON = 1e-6

const guardByTargetId = new Map<string, TargetMoveGuardState>()

const areCoordinatesEqual = (
  left: TargetCoordinates,
  right: TargetCoordinates,
) =>
  Math.abs(left.lat - right.lat) <= TARGET_MOVE_COORDINATE_EPSILON &&
  Math.abs(left.lng - right.lng) <= TARGET_MOVE_COORDINATE_EPSILON

export const beginTargetMoveSyncGuard = (
  targetId: string,
  position: TargetCoordinates,
) => {
  guardByTargetId.set(targetId, {
    phase: "dragging",
    position,
  })
}

export const updateTargetMoveSyncGuard = (
  targetId: string,
  position: TargetCoordinates,
) => {
  guardByTargetId.set(targetId, {
    phase: "dragging",
    position,
  })
}

export const settleTargetMoveSyncGuard = (
  targetId: string,
  position: TargetCoordinates,
  now = Date.now(),
) => {
  guardByTargetId.set(targetId, {
    phase: "settling",
    position,
    expiresAt: now + TARGET_MOVE_SETTLE_WINDOW_MS,
  })
}

export const clearTargetMoveSyncGuard = (targetId: string) => {
  guardByTargetId.delete(targetId)
}

export const clearMissingTargetMoveSyncGuards = (
  liveTargetIds: Iterable<string>,
) => {
  const liveTargetIdSet = new Set(liveTargetIds)

  for (const targetId of guardByTargetId.keys()) {
    if (!liveTargetIdSet.has(targetId)) {
      guardByTargetId.delete(targetId)
    }
  }
}

export const clearAllTargetMoveSyncGuards = () => {
  guardByTargetId.clear()
}

/** Ignore live location SSE while dragging or inside the post-commit settle window. */
export const shouldDeferTargetLocationPatchFromSse = (targetId: string) => {
  const guard = guardByTargetId.get(targetId)
  if (!guard) return false
  if (guard.phase === "dragging") return true
  return Date.now() < guard.expiresAt
}
